import type { ZodType } from '@customerio/hydra-validation';

/**
 * Lightweight entity bundle.
 *
 * Encapsulates identity (name, pk function, **scope**), the bidirectional Zod
 * schemas, the **relation declarations** (all four shapes in one place),
 * and the hierarchical query-key factory. **Does NOT generate hooks.**
 * Per-query option factories and hooks live next to the entity (in
 * `queries.ts` and `mutations.ts`) so callers can compose them directly
 * with `useQuery` / `useSuspenseQuery` / `prefetchQuery` / `useQueries`
 * and override per-call options without going through a factory wrapper.
 *
 * Pattern from
 * https://github.com/alan2207/bulletproof-react/blob/master/apps/react-vite/src/features/discussions/api/get-discussion.ts
 */

/* ------------------------------------------------------------------------- */
/* Scope                                                                      */
/* ------------------------------------------------------------------------- */

/**
 * Scope determines the prefix on every cache key for this entity:
 *
 *   - `workspace`: `['workspace', workspaceId, name, ...]` — workspace-scoped
 *     resources (campaigns, actions, tags, segments, …). Most entities.
 *     `qc.removeQueries({ queryKey: ['workspace', prevId] })` wipes one
 *     workspace's cache on workspace switch.
 *
 *   - `account`: `['account', accountId, name, ...]` — account-scoped resources
 *     (user, plan, billing-info, …). Persist across workspace switches.
 *
 *   - `global`: `[name, ...]` — no scope prefix. For things that genuinely
 *     have no owning context (rare; the auth/login surface is the canonical
 *     example).
 *
 * Cleared independently, addressable by prefix, no chance of one workspace's
 * data accidentally serving another.
 */
export type EntityScope = 'workspace' | 'account' | 'global';

/* ------------------------------------------------------------------------- */
/* Relation declarations — all four shapes in one place                       */
/* ------------------------------------------------------------------------- */

/**
 * Two relation kinds, ActiveRecord/Ember Data style:
 *
 *   - `belongs-to` — single-cardinality. Self carries an FK to one parent.
 *     **No cache slot** (the FK on self IS the index). Consumers follow
 *     with hand-written helpers like `useCampaignForAction(action)`.
 *
 *       action.relations.campaign = {
 *         kind: 'belongs-to', target: 'campaign', via: 'campaign_id'
 *       };
 *
 *   - `has-many` — collection-cardinality. Cache slot lists the related
 *     entities. `via` describes WHERE the FK lives:
 *
 *       - `via: { on: 'self', field: 'tag_ids' }` — self has an array of
 *         target ids (one side of a many-to-many). Slot is authoritative
 *         from the parent's response.
 *
 *           campaign.relations.tags = {
 *             kind: 'has-many', target: 'tag',
 *             via: { on: 'self', field: 'tag_ids' },
 *           };
 *
 *       - `via: { on: 'target', field: 'campaign_id' }` — each target
 *         carries an FK back to me (a 1:N from my side). Slot is
 *         authoritative from the parent's CampaignModel response since
 *         the children arrive sideloaded.
 *
 *           campaign.relations.actions = {
 *             kind: 'has-many', target: 'action',
 *             via: { on: 'target', field: 'campaign_id' },
 *           };
 *
 *       - `via: { on: 'target', field: 'tag_ids' }` — the target has an
 *         array including me (the OTHER side of a many-to-many; e.g.
 *         tag → campaigns is implied by every campaign's `tag_ids`).
 *         Slot is computed by walking sideloads of the opposite entity
 *         (merge semantics; see `mergeCampaignIntoTagInverses`).
 *
 *           tag.relations.campaigns = {
 *             kind: 'has-many', target: 'campaign',
 *             via: { on: 'target', field: 'tag_ids' },
 *           };
 *
 * Two declarations facing each other (campaign.tags + tag.campaigns)
 * model what an ORM would call a many-to-many relationship — there's no
 * separate "many-to-many" kind; the join is the pair of `has-many`
 * declarations.
 *
 * **Cache implications:**
 *   - `belongs-to` → no cache slot; types exclude it from `keys.relation(...)`.
 *   - `has-many` → cache slot `keys.relation(wsId, name, parentId)`.
 *
 * The populator's behavior diverges based on `via.on`:
 *   - `'self'` or `'target'` with single-id field → authoritative replace
 *     from the response.
 *   - `'target'` with array field → merge from sideloads of the opposite
 *     entity; never authoritative without a dedicated endpoint.
 *
 * **Where the mapping actually runs.** Today the populate helpers in
 * `campaign/populate.ts` reference `tag_ids` etc. as string literals.
 * The `via` field on the declaration documents the same thing — and a
 * future generic populator could read these declarations to drive
 * propagation without per-cluster hand-coding. For the pilot, the
 * declarations are the schema; the populator is still imperative. They
 * must agree; code review enforces.
 */
export interface HasManyVia<TField extends string = string> {
  /** Whose field holds the FK — `'self'` (this entity) or `'target'` (the other). */
  readonly on: 'self' | 'target';
  /** FK field name on whichever side `on` selects. May be a single id or an id array. */
  readonly field: TField;
}

export type RelationDefinition<TTarget extends string = string, TField extends string = string> =
  | { readonly kind: 'belongs-to'; readonly target: TTarget; readonly via: TField }
  | { readonly kind: 'has-many'; readonly target: TTarget; readonly via: HasManyVia<TField> };

/** Relation map: `{ [relationName]: RelationDefinition }`. */
export type Relations = Readonly<Record<string, RelationDefinition>>;

/* ------------------------------------------------------------------------- */
/* Entity definition                                                          */
/* ------------------------------------------------------------------------- */

interface EntityDefinition<TEntity, TWriteInput, TRelations extends Relations | undefined, TScope extends EntityScope> {
  name: string;
  scope: TScope;
  pk: (entity: TEntity) => EntityId;
  schema: {
    /** Parses an API response into the entity shape (epoch seconds → Date, etc). */
    read: ZodType<TEntity, unknown>;
    /** Optional inverse schema for outgoing bodies (Date → epoch seconds, strip undefined). */
    write?: ZodType<TWriteInput, unknown>;
  };
  /**
   * Relations this entity participates in, keyed by relation name. See
   * `RelationDefinition` for the four shapes. Use `as const` so TypeScript
   * narrows the discriminated union and `keys.relation(...)` types
   * correctly.
   *
   *   const action = defineEntity({
   *     ...,
   *     relations: {
   *       campaign: { kind: 'belongs-to', via: 'campaign_id' },
   *     },
   *   } as const);
   *
   *   const campaign = defineEntity({
   *     ...,
   *     relations: {
   *       actions: { kind: 'has-many' },
   *       tags: { kind: 'many-to-many' },
   *       subscription_topics: { kind: 'many-to-many' },
   *     } as const,
   *   });
   */
  relations?: TRelations;
}

/* ------------------------------------------------------------------------- */
/* Scope-typed key factories                                                  */
/* ------------------------------------------------------------------------- */

type ScopeId<TScope extends EntityScope> = TScope extends 'workspace' | 'account' ? number : never;

/**
 * pk type — every entity in this layer normalizes ids to `string`,
 * even when the wire ships them as numbers (e.g. `Tag.id`, transformed
 * at parse time). The union with `number` is kept so the type stays
 * compatible if a future entity needs integer-keyed cache keys, but
 * the convention is **string everywhere**: it future-proofs against
 * backend custom-id formats and lets us prefix temporary clones
 * (`'tmp-7'`) without hitting a type wall.
 */
export type EntityId = string | number;
type ScopePrefix<TScope extends EntityScope> = TScope extends 'workspace'
  ? readonly ['workspace', number]
  : TScope extends 'account'
    ? readonly ['account', number]
    : readonly [];

/**
 * Hierarchical query-key factory per
 * https://tkdodo.eu/blog/effective-react-query-keys.
 *
 * Each method returns a key with strictly more specificity than the one
 * above it. TanStack Query matches keys by prefix, so:
 *
 *   - `qc.invalidateQueries({ queryKey: keys.all(wsId) })` invalidates
 *     everything for this entity — list slots and details.
 *   - `qc.invalidateQueries({ queryKey: keys.lists(wsId) })` invalidates
 *     just the list slots; leaves detail caches alone. Use after CREATE
 *     or any change that shifts what appears in lists.
 *   - `qc.setQueryData(keys.detail(wsId, id), updated)` writes a specific
 *     entity's detail cache. Use in mutation onSuccess.
 *
 * The "parent prefix" methods (`all`, `lists`, `details`) exist alongside
 * the leaf methods (`list`, `detail`) so prefix-matched invalidation
 * works — without them, callers would hand-roll prefix arrays.
 *
 * **No `relation` method**: relation views are derived at read time from
 * FK arrays into the entity detail caches (see `useEntitiesByIds`),
 * not stored as separate slots. See doc on `RelationDefinition` and
 * `populate.ts` for why.
 */
export interface EntityKeys<TListParams, TScope extends EntityScope = EntityScope> {
  /** Scope discriminator — exposed so callers can branch if they care. */
  scope: TScope;

  /**
   * Top-level prefix for this entity in this scope.
   *
   *   workspace: `['workspace', wsId, 'campaign']`
   *   account:   `['account', accId, 'user']`
   *   global:    `['settings']`
   *
   * Use to invalidate **everything** about this entity in this scope:
   *   `qc.invalidateQueries({ queryKey: campaign.keys.all(wsId) })`
   * Or to wipe on workspace switch:
   *   `qc.removeQueries({ queryKey: ['workspace', prevWsId] })`  (broader)
   *   `qc.removeQueries({ queryKey: campaign.keys.all(prevWsId) })` (per-entity)
   */
  all: TScope extends 'global'
    ? () => readonly [string]
    : (scopeId: ScopeId<TScope>) => readonly [...ScopePrefix<TScope>, string];

  /**
   * Prefix matching **all paginated/filtered list responses** for this
   * entity. Returns the same prefix `list(...)` produces, minus the params
   * leaf — so `invalidateQueries({ queryKey: keys.lists(wsId) })` matches
   * every cached `list({page: 1})`, `list({page: 2, state: 'running'})`, etc.
   *
   * Use after a CREATE or any change that may have shifted what appears
   * in lists — refreshes every page without having to enumerate filter
   * combinations.
   */
  lists: TScope extends 'global'
    ? () => readonly [string, 'list']
    : (scopeId: ScopeId<TScope>) => readonly [...ScopePrefix<TScope>, string, 'list'];

  /**
   * The cache slot for one specific list query (specific filters/sort/page).
   * Each unique `params` value is its own slot — so navigating between
   * pages keeps both pages cached, and going back is instant.
   */
  list: TScope extends 'global'
    ? (params: TListParams) => readonly [string, 'list', TListParams]
    : (scopeId: ScopeId<TScope>, params: TListParams) => readonly [...ScopePrefix<TScope>, string, 'list', TListParams];

  /**
   * Prefix matching **all single-entity detail caches** for this entity.
   * Same role as `lists` but for the `detail(id)` family.
   */
  details: TScope extends 'global'
    ? () => readonly [string, 'detail']
    : (scopeId: ScopeId<TScope>) => readonly [...ScopePrefix<TScope>, string, 'detail'];

  /**
   * The cache slot for ONE entity's detail. Pre-populating this via
   * `setQueryData` from a list response is what makes the by-id cache
   * cross-check work: when `useDetail(id)` mounts, TanStack Query sees
   * fresh data here and does not fetch.
   *
   * Relation views (`useCampaignActions(c)`, `useCampaignTags(c)`, etc.)
   * read THROUGH this slot — given a parent entity carrying FK arrays,
   * they resolve each id by reading the corresponding child's detail.
   * No separate relation cache slot.
   */
  detail: TScope extends 'global'
    ? (id: EntityId) => readonly [string, 'detail', EntityId]
    : (scopeId: ScopeId<TScope>, id: EntityId) => readonly [...ScopePrefix<TScope>, string, 'detail', EntityId];
}

function makeEntityKeys<TListParams, TScope extends EntityScope>(
  name: string,
  scope: TScope,
): EntityKeys<TListParams, TScope> {
  if (scope === 'global') {
    const factory = {
      scope,
      all: () => [name] as const,
      lists: () => [name, 'list'] as const,
      list: (params: TListParams) => [name, 'list', params] as const,
      details: () => [name, 'detail'] as const,
      detail: (id: EntityId) => [name, 'detail', id] as const,
    };
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return factory as unknown as EntityKeys<TListParams, TScope>;
  }

  const prefix: 'workspace' | 'account' = scope;
  const factory = {
    scope,
    all: (scopeId: number) => [prefix, scopeId, name] as const,
    lists: (scopeId: number) => [prefix, scopeId, name, 'list'] as const,
    list: (scopeId: number, params: TListParams) => [prefix, scopeId, name, 'list', params] as const,
    details: (scopeId: number) => [prefix, scopeId, name, 'detail'] as const,
    detail: (scopeId: number, id: EntityId) => [prefix, scopeId, name, 'detail', id] as const,
  };
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return factory as unknown as EntityKeys<TListParams, TScope>;
}

/* ------------------------------------------------------------------------- */
/* Entity bundle returned by defineEntity                                     */
/* ------------------------------------------------------------------------- */

export interface Entity<
  TEntity,
  TWriteInput,
  TListParams,
  TRelations extends Relations | undefined,
  TScope extends EntityScope,
> {
  /** Stable identifier — used in keys and error messages. */
  name: string;
  /** Scope discriminator — `'workspace' | 'account' | 'global'`. */
  scope: TScope;
  /** Extract pk from a parsed entity. */
  pk: (entity: TEntity) => EntityId;
  /** Hierarchical query-key factory for this entity, scoped per `scope`. */
  keys: EntityKeys<TListParams, TScope>;
  /** Parse an API response into the entity shape. Throws on invalid input. */
  parse: (raw: unknown) => TEntity;
  /** Serialize an entity (or partial) into the wire shape. Throws on invalid input. */
  serialize: (input: unknown) => TWriteInput;
  /** Original schemas — for callers that need direct Zod access. */
  schema: EntityDefinition<TEntity, TWriteInput, TRelations, TScope>['schema'];
  /**
   * Relation declarations, by name. Both kinds (`belongs-to` and
   * `has-many` with `via.on: 'self' | 'target'`) live here.
   * Self-documenting; consumers may inspect at runtime for tooling.
   * Doesn't drive cache infrastructure — relation views are derived at
   * read time from the FK arrays referenced by `via`.
   */
  relations: TRelations extends Relations ? TRelations : Record<string, never>;
}

export function defineEntity<
  TEntity,
  TWriteInput = TEntity,
  TListParams = void,
  TRelations extends Relations | undefined = undefined,
  TScope extends EntityScope = 'workspace',
>(
  def: EntityDefinition<TEntity, TWriteInput, TRelations, TScope>,
): Entity<TEntity, TWriteInput, TListParams, TRelations, TScope> {
  const keys = makeEntityKeys<TListParams, TScope>(def.name, def.scope);
  return {
    name: def.name,
    scope: def.scope,
    pk: def.pk,
    keys,
    parse: (raw: unknown): TEntity => def.schema.read.parse(raw),
    serialize: (input: unknown): TWriteInput => {
      if (def.schema.write == null) {
        throw new Error(`${def.name}: serialize() called but no write schema is defined.`);
      }
      return def.schema.write.parse(input);
    },
    schema: def.schema,
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    relations: (def.relations ?? {}) as TRelations extends Relations ? TRelations : Record<string, never>,
  };
}
